import {
  Add,
  Business,
  CallMerge,
  Group,
  LocalShipping,
  Place,
  Search,
} from '@mui/icons-material';
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  DataGridPro,
  GridApiPro,
  GridColDef,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ContactCompany } from '@otw/models/leads/contactCompanies';
import type { PickupLocation } from '@otw/models';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { usePermissions } from 'hooks/auth/usePermissions';
import { useContactCompanies } from 'hooks/contacts/useContactCompanies';
import { useContacts } from 'hooks/contacts/useContacts';
import {
  getUserDisplayName,
  useInventoryAssignableUsers,
  useSalesAssignableUsers,
  useUsers,
} from 'hooks/useUsers';
import { usePickupLocations } from 'hooks/inventory/usePickupLocations';
import { useTerminals } from 'hooks/terminals/useTerminals';
import { getTerminalDisplayName, buildTerminalSearchText } from '@otw/models/leads/terminals';
import { resolveTerminalLocation } from '@/core/terminals/terminalAddress';
import { getCompanyTerminals } from 'views/SuperAdmin/Management/TerminalManagement/util/terminalCompanyLinks';
import {
  formatPickupLocationAddress,
} from '../../util/pickupLocationDisplay';
import { formatLastActivityDateTime } from '../../util/activityDisplay';
import { openCompanyDetailInNewTab } from '../../util/companyRoutes';
import { formatNumber } from '../../util/customerUtils';
import { useIsMobile } from 'hooks/screen/useIsMobile';
import {
  CHIP_FONT,
  CHIP_HEIGHT_DENSE,
  CHIP_MAX_WIDTH,
  TOUCH_MIN,
} from 'core/theme.tokens';
import { buildOwnerDisplayNameIndex } from '../../util/resolveOwnerDisplayName';
import { ContactCompanyFormDialog, MergeCompaniesDialog } from '../dialogs';
import ContactCompanyPreviewPane from '../ContactCompanyPreviewPane';
import ListBackToTopBar from '../ListBackToTopBar';
import { CompanyListCard } from '../cards';
import { CrmPageActions } from '../CrmPageChrome';

interface CompaniesTabProps {
  isActive: boolean;
}

type OwnerFilter = 'all' | 'unassigned' | string;

interface CompanyRow {
  id: string;
  name: string;
  ownerId: ContactCompany['ownerId'];
  ownerName: string;
  memberNames: string[];
  memberSearchText: string;
  memberCount: number;
  terminalNames: string[];
  terminalSearchText: string;
  businessAddress: string;
  dropoffAddress: string;
  dropoffExtraCount: number;
  dropoffAddressTitle: string;
  website: string;
  lastActivityAt: Date | null;
}

const SEARCH_DEBOUNCE_MS = 300;
const COMPANIES_LIST_HEIGHT = '75vh';
const MOBILE_CARD_ESTIMATE_PX = 152;

const mobileListCardChromeSx = {
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  '& .MuiChip-root': {
    maxWidth: CHIP_MAX_WIDTH,
    height: CHIP_HEIGHT_DENSE,
    fontSize: CHIP_FONT,
    fontWeight: 600,
    '& .MuiChip-label': {
      px: 0.75,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  '& .MuiButton-root': { minHeight: TOUCH_MIN },
  '& .MuiIconButton-root': { minWidth: TOUCH_MIN, minHeight: TOUCH_MIN },
} as const;

const useDebouncedValue = (value: string, delayMs: number): string => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
};

const CompaniesTab: React.FC<CompaniesTabProps> = ({ isActive }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { userInfo } = useAuth();
  const { leadManagementScope } = usePermissions();
  const { companies, isLoading, mergeCompanies, isMerging } = useContactCompanies();
  const { contacts } = useContacts();
  const { pickupLocations } = usePickupLocations();
  const { terminals } = useTerminals();
  const { users: salesAssignableOwners } = useSalesAssignableUsers();
  const { users: inventoryAssignableOwners } = useInventoryAssignableUsers();
  const { users: allUsers } = useUsers();

  const assignableOwners = useMemo(() => {
    const seen = new Set<string>();
    const list: typeof salesAssignableOwners = [];
    for (const user of [...salesAssignableOwners, ...inventoryAssignableOwners]) {
      const id = String(user.id);
      if (seen.has(id)) continue;
      seen.add(id);
      list.push(user);
    }
    return list.sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b)));
  }, [salesAssignableOwners, inventoryAssignableOwners]);

  const contactNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const contact of contacts) {
      map.set(contact.id, contact.name?.trim() || 'Unnamed contact');
    }
    return map;
  }, [contacts]);

  const pickupLocationById = useMemo(() => {
    const map = new Map<string, PickupLocation>();
    for (const location of pickupLocations ?? []) {
      if (location.id) map.set(location.id, location);
    }
    return map;
  }, [pickupLocations]);

  const ownerNameById = useMemo(
    () => buildOwnerDisplayNameIndex(allUsers),
    [allUsers],
  );

  const resolveOwnerName = useCallback(
    (ownerId: ContactCompany['ownerId']): string => {
      if (ownerId === null || ownerId === undefined || ownerId === '') return 'Unassigned';
      return ownerNameById.get(String(ownerId)) ?? 'Unknown';
    },
    [ownerNameById],
  );

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<ContactCompany | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [mergePickMode, setMergePickMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState<string[]>([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const mobileListRef = useRef<HTMLDivElement>(null);
  const desktopGridApiRef = useGridApiRef();

  const scrollDesktopListToTop = useCallback(() => {
    const root = desktopGridApiRef.current?.rootElementRef?.current;
    const scroller = root?.querySelector(
      '.MuiDataGrid-virtualScroller',
    ) as HTMLElement | null;
    scroller?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [desktopGridApiRef]);

  const scrollMobileListToTop = useCallback(() => {
    mobileListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const exitMergePickMode = useCallback(() => {
    setMergePickMode(false);
    setMergeSelection([]);
  }, []);

  const handleMergeButtonClick = useCallback(() => {
    if (!mergePickMode) {
      setMergePickMode(true);
      setMergeSelection([]);
      return;
    }
    if (mergeSelection.length === 2) {
      setMergeDialogOpen(true);
    }
  }, [mergePickMode, mergeSelection.length]);

  const handleToggleMergeSelect = useCallback((id: string) => {
    if (!mergePickMode) return;
    setMergeSelection((current) => {
      if (current.includes(id)) {
        return current.filter((candidate) => candidate !== id);
      }
      const next = [...current, id];
      return next.length <= 2 ? next : next.slice(-2);
    });
  }, [mergePickMode]);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === selectedCompanyId) ?? null,
    [companies, selectedCompanyId],
  );

  const scoped = useMemo(() => {
    if (leadManagementScope === 'none') return [];
    if (leadManagementScope === 'all') return companies;
    if (!userInfo?.id) return [];
    return companies.filter(
      (c) => c.ownerId == null || String(c.ownerId) === String(userInfo.id),
    );
  }, [companies, leadManagementScope, userInfo?.id]);

  const rows = useMemo<CompanyRow[]>(
    () =>
      scoped.map((c) => {
        const memberNames = c.members
          .map((member) => contactNameById.get(member.contactId) ?? 'Unknown contact')
          .sort((a, b) => a.localeCompare(b));
        const companyTerminals = getCompanyTerminals(c, terminals);
        const terminalNames = companyTerminals.map((terminal) =>
          getTerminalDisplayName(
            terminal,
            resolveTerminalLocation(terminal, pickupLocations),
          ),
        );
        const dropoffLabels = (c.dropoffLocationIds ?? [])
          .map((id) => formatPickupLocationAddress(pickupLocationById.get(id.trim())))
          .filter((label) => label.length > 0);
        return {
          id: c.id,
          name: c.name || 'Unnamed company',
          ownerId: c.ownerId ?? null,
          ownerName: resolveOwnerName(c.ownerId),
          memberNames,
          memberSearchText: memberNames.join(' ').toLowerCase(),
          memberCount: memberNames.length,
          terminalNames,
          terminalSearchText: companyTerminals
            .map((terminal) =>
              buildTerminalSearchText(
                terminal,
                resolveTerminalLocation(terminal, pickupLocations),
              ),
            )
            .join(' '),
          businessAddress: formatPickupLocationAddress(
            c.businessAddressId
              ? pickupLocationById.get(c.businessAddressId.trim())
              : undefined,
          ),
          dropoffAddress: dropoffLabels[0] ?? '',
          dropoffExtraCount: Math.max(0, dropoffLabels.length - 1),
          dropoffAddressTitle: dropoffLabels.join(', '),
          website: c.website ?? '',
          lastActivityAt: c.lastActivityAt ?? null,
        };
      }),
    [scoped, pickupLocationById, contactNameById, resolveOwnerName, terminals, pickupLocations],
  );

  const visibleRows = useMemo(() => {
    const q = debouncedSearchQuery.trim().toLowerCase();
    return rows.filter((row) => {
      if (ownerFilter !== 'all') {
        const isUnassigned =
          row.ownerId === null || row.ownerId === undefined || row.ownerId === '';
        if (ownerFilter === 'unassigned') {
          if (!isUnassigned) return false;
        } else if (String(row.ownerId) !== String(ownerFilter)) {
          return false;
        }
      }
      if (!q) return true;
      return (
        row.name.toLowerCase().includes(q) ||
        row.ownerName.toLowerCase().includes(q) ||
        row.memberSearchText.includes(q) ||
        row.terminalSearchText.includes(q) ||
        row.businessAddress.toLowerCase().includes(q) ||
        row.dropoffAddress.toLowerCase().includes(q) ||
        row.website.toLowerCase().includes(q)
      );
    });
  }, [rows, debouncedSearchQuery, ownerFilter]);

  const sortedVisibleRows = useMemo(
    () =>
      [...visibleRows].sort((a, b) => {
        const aMs = a.lastActivityAt?.getTime() ?? -Infinity;
        const bMs = b.lastActivityAt?.getTime() ?? -Infinity;
        return bMs - aMs;
      }),
    [visibleRows],
  );

  const mobileRowVirtualizer = useVirtualizer({
    count: sortedVisibleRows.length,
    getScrollElement: () => mobileListRef.current,
    estimateSize: () => MOBILE_CARD_ESTIMATE_PX,
    overscan: 6,
  });

  const handleOpenCompanyPage = useCallback(
    (companyId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      openCompanyDetailInNewTab(companyId);
    },
    [],
  );

  const totals = useMemo(() => {
    const total = scoped.length;
    const totalContacts = scoped.reduce((sum, c) => sum + c.members.length, 0);
    const claimed = scoped.filter(
      (c) => c.ownerId !== null && c.ownerId !== undefined && c.ownerId !== '',
    ).length;
    const withDropoff = scoped.filter((c) => (c.dropoffLocationIds?.length ?? 0) > 0).length;
    return { total, totalContacts, claimed, withDropoff };
  }, [scoped]);

  const columns = useMemo<GridColDef<CompanyRow>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Company',
        flex: 1.4,
        minWidth: 260,
        renderCell: (params) => (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ minWidth: 0, width: '100%' }}
          >
            <Avatar
              variant="rounded"
              onClick={(event) => handleOpenCompanyPage(params.row.id, event)}
              sx={{
                width: 32,
                height: 32,
                bgcolor: alpha(theme.palette.primary.main, 0.16),
                color: theme.palette.primary.main,
                fontSize: 14,
                fontWeight: 700,
                flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              {params.row.name?.charAt(0)?.toUpperCase() || '?'}
            </Avatar>
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              onClick={(event) => handleOpenCompanyPage(params.row.id, event)}
              sx={{
                flex: 1,
                minWidth: 0,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {params.row.name}
            </Typography>
            <Button
              className="company-preview-action"
              size="small"
              variant="outlined"
              onClick={(event) => {
                event.stopPropagation();
                setSelectedCompanyId(params.row.id);
              }}
              sx={{
                flexShrink: 0,
                py: 0.5,
                px: 1,
                minWidth: 0,
                fontSize: 12,
                lineHeight: 1.4,
                borderRadius: 2,
              }}
            >
              Preview
            </Button>
          </Stack>
        ),
      },
      {
        field: 'memberNames',
        headerName: 'Contacts',
        flex: 1.1,
        minWidth: 200,
        valueGetter: (_value, row) => row.memberNames.join(', '),
        sortComparator: (a: string, b: string) => a.localeCompare(b),
        renderCell: (params) => {
          const names = params.row.memberNames;
          if (names.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }
          const visible = names.slice(0, 2);
          const overflow = names.length - visible.length;
          return (
            <Tooltip title={names.join(', ')} placement="top" enterDelay={400}>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ minWidth: 0, width: '100%', overflow: 'hidden' }}
              >
                {visible.map((name) => (
                  <Chip
                    key={name}
                    size="small"
                    label={name}
                    sx={{
                      height: 22,
                      fontSize: 12,
                      fontWeight: 600,
                      maxWidth: names.length === 1 ? '100%' : '48%',
                      bgcolor: alpha(theme.palette.success.main, 0.12),
                      color: theme.palette.success.dark,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        px: 1,
                      },
                    }}
                  />
                ))}
                {overflow > 0 ? (
                  <Chip
                    size="small"
                    label={`+${overflow}`}
                    sx={{
                      height: 22,
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                      bgcolor: alpha(theme.palette.text.secondary, 0.1),
                      color: 'text.secondary',
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                ) : null}
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: 'terminalNames',
        headerName: 'Terminals',
        flex: 1,
        minWidth: 160,
        valueGetter: (_value, row) => row.terminalNames.join(', '),
        sortComparator: (a: string, b: string) => a.localeCompare(b),
        renderCell: (params) => {
          const names = params.row.terminalNames;
          if (names.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }
          const visible = names.slice(0, 2);
          const overflow = names.length - visible.length;
          return (
            <Tooltip title={names.join(', ')} placement="top" enterDelay={400}>
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ minWidth: 0, width: '100%', overflow: 'hidden' }}
              >
                {visible.map((name) => (
                  <Chip
                    key={name}
                    size="small"
                    label={name}
                    sx={{
                      height: 22,
                      fontSize: 12,
                      fontWeight: 600,
                      maxWidth: names.length === 1 ? '100%' : '48%',
                      bgcolor: alpha(theme.palette.info.main, 0.12),
                      color: theme.palette.info.dark,
                      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        px: 1,
                      },
                    }}
                  />
                ))}
                {overflow > 0 ? (
                  <Chip
                    size="small"
                    label={`+${overflow}`}
                    sx={{
                      height: 22,
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                      bgcolor: alpha(theme.palette.text.secondary, 0.1),
                      color: 'text.secondary',
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                ) : null}
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: 'ownerName',
        headerName: 'Company owner',
        flex: 0.9,
        minWidth: 160,
        renderCell: (params) => (
          <Typography
            variant="body2"
            color={params.value === 'Unassigned' ? 'text.secondary' : 'text.primary'}
            noWrap
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'businessAddress',
        headerName: 'Business Address',
        flex: 0.9,
        minWidth: 160,
        renderCell: (params) =>
          params.value ? (
            <Typography variant="body2" noWrap>
              {params.value}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: 'dropoffAddress',
        headerName: 'Dropoff Address',
        flex: 1.2,
        minWidth: 220,
        renderCell: (params) =>
          params.value ? (
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ minWidth: 0, width: '100%' }}
              title={params.row.dropoffAddressTitle}
            >
              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {params.value}
              </Typography>
              {params.row.dropoffExtraCount > 0 && (
                <Chip
                  size="small"
                  label={`+${params.row.dropoffExtraCount}`}
                  sx={{
                    height: 18,
                    fontSize: 11,
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    '& .MuiChip-label': { px: 0.5 },
                  }}
                />
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: 'lastActivityAt',
        headerName: 'Last activity',
        flex: 0.9,
        minWidth: 180,
        valueGetter: (_value, row) => row.lastActivityAt ?? null,
        sortComparator: (a: Date | null, b: Date | null) => {
          const aMs = a?.getTime() ?? -Infinity;
          const bMs = b?.getTime() ?? -Infinity;
          return aMs - bMs;
        },
        renderCell: (params) => (
          <Typography
            variant="body2"
            color={params.row.lastActivityAt ? 'text.primary' : 'text.secondary'}
            noWrap
          >
            {formatLastActivityDateTime(params.row.lastActivityAt)}
          </Typography>
        ),
      },
    ],
    [handleOpenCompanyPage, theme.palette],
  );

  if (!isActive) return null;

  const kpi = (
    icon: React.ReactNode,
    label: string,
    value: React.ReactNode,
    accent: string,
  ) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderRadius: 2,
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(accent, 0.02)} 100%)`,
        borderColor: alpha(accent, 0.25),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: alpha(accent, 0.18),
            color: accent,
            width: 32,
            height: 32,
            '& .MuiSvgIcon-root': { fontSize: 18 },
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.65rem' }}
          >
            {label}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.15, fontSize: '1rem' }} noWrap>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ pb: 2, overflowX: 'hidden', maxWidth: '100%' }}>
      <CrmPageActions active={isActive}>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ justifyContent: 'flex-end' }}>
          {mergePickMode ? (
            <Button variant="text" onClick={exitMergePickMode}>
              Cancel
            </Button>
          ) : null}
          <Button
            variant="outlined"
            startIcon={<CallMerge />}
            disabled={mergePickMode && mergeSelection.length !== 2}
            onClick={handleMergeButtonClick}
          >
            {!mergePickMode
              ? 'Merge companies'
              : mergeSelection.length === 2
                ? 'Merge (2 selected)'
                : `Select 2 (${mergeSelection.length}/2)`}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditingCompany(null);
              setIsFormOpen(true);
            }}
          >
            Add company
          </Button>
        </Stack>
      </CrmPageActions>

      {/* KPIs */}
      <Grid container spacing={1} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}>
          {kpi(<Business />, 'Total companies', formatNumber(totals.total), theme.palette.primary.main)}
        </Grid>
        <Grid item xs={6} md={3}>
          {kpi(
            <Group />,
            'Linked contacts',
            formatNumber(totals.totalContacts),
            theme.palette.info.main,
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          {kpi(
            <Place />,
            'Claimed',
            formatNumber(totals.claimed),
            theme.palette.warning.main,
          )}
        </Grid>
        <Grid item xs={6} md={3}>
          {kpi(
            <LocalShipping />,
            'With dropoff',
            formatNumber(totals.withDropoff),
            theme.palette.success.main,
          )}
        </Grid>
      </Grid>

      {/* Search */}
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          mb: 1.5,
          borderRadius: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          alignItems: { sm: 'center' },
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search by company, contact, owner, location, dropoff, or website"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <TextField
          select
          size="small"
          label="Company owner"
          value={ownerFilter}
          onChange={(e) => setOwnerFilter(e.target.value as OwnerFilter)}
          sx={{ minWidth: { xs: '100%', sm: 220 } }}
        >
          <MenuItem value="all">All owners</MenuItem>
          <MenuItem value="unassigned">Unassigned</MenuItem>
          {assignableOwners.map((user) => (
            <MenuItem key={user.id} value={String(user.id)}>
              {getUserDisplayName(user)}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {mergePickMode ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Select exactly two companies to merge, then click{' '}
          <strong>Merge</strong>.
        </Alert>
      ) : null}

      {/* Grid + optional preview pane */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            alignItems: { xs: 'stretch', md: 'flex-start' },
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              minWidth: 0,
              height: COMPANIES_LIST_HEIGHT,
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
            }}
          >
            <DataGridPro<CompanyRow>
              apiRef={desktopGridApiRef as React.MutableRefObject<GridApiPro>}
              rows={visibleRows}
              columns={columns}
              density="compact"
              checkboxSelection={mergePickMode}
              disableRowSelectionOnClick
              pagination={false}
              hideFooter
              rowSelectionModel={mergePickMode ? mergeSelection : []}
              onRowSelectionModelChange={(model: GridRowSelectionModel) => {
                if (!mergePickMode) return
                const ids = (Array.isArray(model) ? model : [model]).map(String)
                setMergeSelection(ids.length <= 2 ? ids : ids.slice(-2))
              }}
              initialState={{
                sorting: { sortModel: [{ field: 'lastActivityAt', sort: 'desc' }] },
              }}
              getRowHeight={() => 64}
              sx={{
                border: 'none',
                flex: 1,
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  fontWeight: 700,
                },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-row .company-preview-action': {
                  opacity: 0,
                  pointerEvents: 'none',
                  transition: 'opacity 0.15s ease-in-out',
                },
                '& .MuiDataGrid-row:hover .company-preview-action, & .MuiDataGrid-row:focus-within .company-preview-action':
                  {
                    opacity: 1,
                    pointerEvents: 'auto',
                  },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                  outline: 'none',
                },
                ...(mergePickMode
                  ? {
                      '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer':
                        {
                          display: 'none',
                        },
                    }
                  : {}),
              }}
              slots={{
                noRowsOverlay: () => (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      {rows.length === 0 ? 'No companies yet' : 'No matches'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      {rows.length === 0
                        ? 'Add companies to keep all related contacts together.'
                        : 'Try a different search or owner filter.'}
                    </Typography>
                  </Stack>
                ),
              }}
            />
            <ListBackToTopBar
              onBackToTop={scrollDesktopListToTop}
              disabled={visibleRows.length === 0}
            />
          </Paper>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              width: '100%',
              display: { xs: 'block', md: 'none' },
            }}
          >
            {sortedVisibleRows.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  borderRadius: 3,
                  textAlign: 'center',
                  bgcolor: alpha(theme.palette.background.default, 0.6),
                }}
              >
                <Typography variant="h6" gutterBottom>
                  {rows.length === 0 ? 'No companies yet' : 'No matches'}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  {rows.length === 0
                    ? 'Add companies to keep all related contacts together.'
                    : 'Try a different search or owner filter.'}
                </Typography>
              </Paper>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  height: COMPANIES_LIST_HEIGHT,
                  borderRadius: 3,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  ref={mobileListRef}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      height: `${mobileRowVirtualizer.getTotalSize()}px`,
                      width: '100%',
                      position: 'relative',
                    }}
                  >
                    {mobileRowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const row = sortedVisibleRows[virtualRow.index];
                      if (!row) return null;
                      return (
                        <Box
                          key={row.id}
                          data-index={virtualRow.index}
                          ref={mobileRowVirtualizer.measureElement}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translateY(${virtualRow.start}px)`,
                            pb: 1.5,
                            ...mobileListCardChromeSx,
                          }}
                        >
                          <CompanyListCard
                            row={row}
                            mergePickMode={mergePickMode}
                            isSelected={mergeSelection.includes(row.id)}
                            onToggleSelect={handleToggleMergeSelect}
                            onPreview={setSelectedCompanyId}
                            onOpenCompanyPage={handleOpenCompanyPage}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
                <ListBackToTopBar onBackToTop={scrollMobileListToTop} />
              </Paper>
            )}
          </Box>
          {selectedCompany ? (
            <ContactCompanyPreviewPane
              company={selectedCompany}
              onClose={() => setSelectedCompanyId(null)}
              onEdit={(c) => {
                setEditingCompany(c);
                setIsFormOpen(true);
              }}
            />
          ) : null}
        </Box>
      )}

      <ContactCompanyFormDialog
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingCompany(null);
        }}
        company={editingCompany}
      />

      <MergeCompaniesDialog
        open={mergeDialogOpen}
        onClose={() => {
          setMergeDialogOpen(false)
        }}
        companies={scoped}
        initialPrimaryId={mergeSelection[0] ?? null}
        initialSecondaryId={mergeSelection[1] ?? null}
        isMerging={isMerging}
        onMerge={async (input) => {
          await mergeCompanies(input)
          setMergeDialogOpen(false)
          exitMergePickMode()
          setSelectedCompanyId((current) =>
            current === input.secondaryCompanyId ? null : current,
          )
        }}
      />
    </Box>
  );
};

export default CompaniesTab;
